// User Context Provider - Premium Personalization Layer
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const CONTEXT_STORAGE_KEY = 'nathof_user_context';
const CONTEXT_EXPIRY_DAYS = 30;

export interface UserSkinProfile {
  persona: string;
  skinType: string;
  contextNiche: string;
  symptoms: string[];
  objectives: string[];
  recommendedProducts: string[];
  quizCompletedAt?: string;
  hydrationScore?: number;
  hydrationCompletedAt?: string;
}

export interface UserContextValue {
  profile: UserSkinProfile | null;
  isContextAvailable: boolean;
  saveProfile: (profile: Partial<UserSkinProfile>) => void;
  clearProfile: () => void;
  getContextBadges: () => ContextBadge[];
  getPersonalizedMessage: () => string | null;
}

export interface ContextBadge {
  id: string;
  label: string;
  icon?: string;
  contexts: string[];
}

// Selos dinâmicos baseados no contexto
export const contextBadges: ContextBadge[] = [
  {
    id: 'high-tolerance',
    label: 'Alta tolerabilidade para pele sensibilizada',
    icon: '✓',
    contexts: ['onco-care', 'barreira-melasma', 'pos-sol', 'maternidade-segura']
  },
  {
    id: 'breastfeeding-safe',
    label: 'Compatível com amamentação',
    icon: '♡',
    contexts: ['maternidade-segura']
  },
  {
    id: 'fragrance-free',
    label: 'Sem fragrância – indicado para pele reativa',
    icon: '◯',
    contexts: ['onco-care', 'barreira-melasma', 'pele-jovem-sensivel', 'dermatite-atopica', 'xerose-idoso']
  },
  {
    id: 'barrier-support',
    label: 'Suporte à barreira cutânea',
    icon: '◈',
    contexts: ['inverno-baixa-umidade', 'barreira-melasma', 'pos-procedimento', 'profissional-saude']
  },
  {
    id: 'clinical-safe',
    label: 'Uso seguro em rotinas clínicas',
    icon: '⊕',
    contexts: ['profissional-saude', 'onco-care']
  },
  {
    id: 'dry-touch',
    label: 'Toque seco e rápida absorção',
    icon: '◇',
    contexts: ['profissional-saude', 'pele-jovem-sensivel', 'viagem-neve-altitude']
  },
  {
    id: 'intensive-hydration',
    label: 'Hidratação intensiva e prolongada',
    icon: '💧',
    contexts: ['inverno-baixa-umidade', 'xerose-idoso', 'menopausa-climaterio']
  }
];

const UserContextContext = createContext<UserContextValue | undefined>(undefined);

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<UserSkinProfile | null>(null);

  // Load context from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(CONTEXT_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          const savedAt = new Date(parsed.savedAt || 0);
          const now = new Date();
          const daysDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
          
          if (daysDiff < CONTEXT_EXPIRY_DAYS) {
            setProfile(parsed.profile);
          } else {
            localStorage.removeItem(CONTEXT_STORAGE_KEY);
          }
        }
      } catch (e) {
        console.error('Failed to load user context:', e);
      }
    }
  }, []);

  const saveProfile = (newProfile: Partial<UserSkinProfile>) => {
    const updatedProfile = { ...profile, ...newProfile } as UserSkinProfile;
    setProfile(updatedProfile);
    
    if (typeof window !== 'undefined') {
      localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify({
        profile: updatedProfile,
        savedAt: new Date().toISOString()
      }));
    }
  };

  const clearProfile = () => {
    setProfile(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CONTEXT_STORAGE_KEY);
    }
  };

  const getContextBadges = (): ContextBadge[] => {
    if (!profile?.contextNiche) return [];
    return contextBadges.filter(badge => 
      badge.contexts.includes(profile.contextNiche)
    );
  };

  const getPersonalizedMessage = (): string | null => {
    if (!profile?.contextNiche) return null;
    
    const messages: Record<string, string> = {
      'onco-care': 'Rotina pensada para pele sensibilizada em tratamento',
      'barreira-melasma': 'Foco em reconstruir a barreira antes de clarear',
      'inverno-baixa-umidade': 'Hidratação intensiva para dias secos',
      'maternidade-segura': 'Cuidado suave para essa fase especial',
      'pos-procedimento': 'Suporte pós-procedimento estético',
      'profissional-saude': 'Proteção para mãos em rotina de lavagem',
      'xerose-idoso': 'Hidratação funcional para pele madura',
      'dermatite-atopica': 'Conforto para pele com tendência atópica',
      'pele-jovem-sensivel': 'Rotina minimalista e funcional',
      'menopausa-climaterio': 'Cuidado para pele que muda com o tempo'
    };
    
    return messages[profile.contextNiche] || null;
  };

  return (
    <UserContextContext.Provider value={{
      profile,
      isContextAvailable: !!profile,
      saveProfile,
      clearProfile,
      getContextBadges,
      getPersonalizedMessage
    }}>
      {children}
    </UserContextContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContextContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
